import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Star,
  ContentCopy,
  Tag,
  Create,
} from '@mui/icons-material';
import { aiContentAPI, postsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../contexts/ClientContext';
import {
  MEDIA_CONTENT_PILLAR_IDS,
  MEDIA_CONTENT_PILLAR_LABELS,
} from '../config/contentPillars';

const AIContent = () => {
  const { showSuccess } = useToast();
  const { clients } = useClients();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Data state
  const [contentTypes, setContentTypes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Post Generation State
  const [postForm, setPostForm] = useState({
    client_id: '',
    content_pillar: 'motivation',
    content_type: 'general',
    platform: 'general',
    tone: 'professional',
    key_message: '',
    num_variations: 3,
  });
  const [generatedPosts, setGeneratedPosts] = useState([]);

  // Hashtag Generation State
  const [hashtagForm, setHashtagForm] = useState({
    content: '',
    client_id: '',
    platform: 'general',
    num_hashtags: 10,
  });
  const [generatedHashtags, setGeneratedHashtags] = useState([]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const contentTypesData = await aiContentAPI.getContentTypes();
        setContentTypes(contentTypesData && typeof contentTypesData === 'object' ? contentTypesData : {});
      } catch (error) {
        setError('Failed to load initial data');
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Post generation handler
  const handlePostGeneration = async () => {
    if (!postForm.client_id) {
      setError('Please select a client');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const posts = await aiContentAPI.generatePosts(postForm);
      setGeneratedPosts(posts);
      showSuccess('Posts generated successfully!');
    } catch (error) {
      setError('Failed to generate posts');
      console.error('Error generating posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Hashtag generation handler
  const handleHashtagGeneration = async () => {
    if (!hashtagForm.client_id || !hashtagForm.content) {
      setError('Please select a client and enter content');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const hashtags = await aiContentAPI.generateHashtags(hashtagForm);
      setGeneratedHashtags(hashtags);
      showSuccess('Hashtags generated successfully!');
    } catch (error) {
      setError('Failed to generate hashtags');
      console.error('Error generating hashtags:', error);
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Keep as post handler
  const handleKeepAsPost = async (post) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      let contentWithCTA = post.content;
      if (post.call_to_action) {
        contentWithCTA = `${post.content}\n\n${post.call_to_action}`;
      }
      const postData = {
        client_id: postForm.client_id,
        platform: postForm.platform,
        content: contentWithCTA,
        hashtags: post.hashtags,
        title: post.title,
        status: 'draft',
      };
      await postsAPI.createPost(postData);
      setSuccess('Post saved as draft! You can find it in the Posts tab.');
    } catch (error) {
      setError('Failed to save post as draft');
      console.error('Error saving post:', error);
    } finally {
      setLoading(false);
    }
  };

  // Simple input handlers - no useCallback to avoid re-render issues
  const handlePostFormChange = (field, value) => {
    setPostForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleHashtagFormChange = (field, value) => {
    setHashtagForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#0d1117' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            color: '#f0f6fc',
            fontWeight: 600,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Star sx={{ fontSize: 32, color: '#58a6ff' }} />
          AI Content Generation
        </Typography>
        <Typography variant="body1" sx={{ color: '#7d8590', mb: 3 }}>
          Generate engaging social media content using AI
        </Typography>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            bgcolor: '#21262d',
            border: '1px solid #f85149',
            color: '#f85149',
            '& .MuiAlert-icon': { color: '#f85149' },
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            bgcolor: '#21262d',
            border: '1px solid #238636',
            color: '#238636',
            '& .MuiAlert-icon': { color: '#238636' },
          }}
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 4, bgcolor: '#161b22', border: '1px solid #30363d' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            bgcolor: '#21262d',
            borderBottom: '1px solid #30363d',
            '& .MuiTab-root': {
              color: '#7d8590',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 64,
              padding: '12px 24px',
              transition: 'all 0.2s',
              '&:hover': {
                color: '#f0f6fc',
                bgcolor: 'rgba(88, 166, 255, 0.1)',
              },
              '&.Mui-selected': {
                color: '#58a6ff',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#58a6ff',
              height: 3,
            },
          }}
        >
          <Tab
            label="Generate Posts"
            icon={<Star sx={{ fontSize: 20, mb: 0.5 }} />}
            iconPosition="top"
          />
          <Tab
            label="Generate Hashtags"
            icon={<Tag sx={{ fontSize: 20, mb: 0.5 }} />}
            iconPosition="top"
          />
        </Tabs>
      </Card>

      {/* Tab Content */}
      <Box
        sx={{
          bgcolor: '#161b22',
          borderRadius: 2,
          border: '1px solid #30363d',
          p: 3,
        }}
      >
        {/* Generate Posts Tab */}
        {activeTab === 0 && (
          <Box>
            <Typography
              variant="h6"
              sx={{ color: '#f0f6fc', mb: 4, fontWeight: 600 }}
            >
              Generate AI-Powered Posts
            </Typography>

            {/* Form Controls */}
            <Card
              sx={{
                p: 4,
                mb: 4,
                bgcolor: '#21262d',
                border: '1px solid #30363d',
              }}
            >
              <Grid container spacing={3} justifyContent="center">
                {/* Client Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Select
                      value={postForm.client_id || ''}
                      onChange={(e) =>
                        handlePostFormChange('client_id', e.target.value)
                      }
                      displayEmpty
                      renderValue={(value) => {
                        if (!value)
                          return (
                            <span style={{ color: '#7d8590' }}>
                              Select Client
                            </span>
                          );
                        const client = clients.find((c) => c.id === value);
                        return client ? client.name : '';
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' },
                        '& .MuiSvgIcon-root': { color: '#8b949e' },
                      }}
                    >
                      {clients.map((client) => (
                        <MenuItem
                          key={client.id}
                          value={client.id}
                          sx={{ color: '#f0f6fc' }}
                        >
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Content pillar (Task 2.3 — prompt templates) */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Select
                      value={postForm.content_pillar || 'motivation'}
                      onChange={(e) =>
                        handlePostFormChange('content_pillar', e.target.value)
                      }
                      displayEmpty
                      renderValue={(value) =>
                        MEDIA_CONTENT_PILLAR_LABELS[value] || value || 'Motivation'
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' },
                        '& .MuiSvgIcon-root': { color: '#8b949e' },
                      }}
                    >
                      {MEDIA_CONTENT_PILLAR_IDS.map((id) => (
                        <MenuItem key={id} value={id} sx={{ color: '#f0f6fc' }}>
                          {MEDIA_CONTENT_PILLAR_LABELS[id] || id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Platform Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Select
                      value={postForm.platform || ''}
                      onChange={(e) =>
                        handlePostFormChange('platform', e.target.value)
                      }
                      displayEmpty
                      renderValue={(value) => {
                        if (!value)
                          return (
                            <span style={{ color: '#7d8590' }}>
                              Select Platform
                            </span>
                          );
                        return value.charAt(0).toUpperCase() + value.slice(1);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' },
                        '& .MuiSvgIcon-root': { color: '#8b949e' },
                      }}
                    >
                      {contentTypes.platforms?.map((platform) => (
                        <MenuItem
                          key={platform}
                          value={platform}
                          sx={{ color: '#f0f6fc' }}
                        >
                          {platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Content Type */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <Select
                      value={postForm.content_type || ''}
                      onChange={(e) =>
                        handlePostFormChange('content_type', e.target.value)
                      }
                      displayEmpty
                      renderValue={(value) => {
                        if (!value)
                          return (
                            <span style={{ color: '#7d8590' }}>
                              Content Type
                            </span>
                          );
                        return (
                          value.charAt(0).toUpperCase() +
                          value.slice(1).replace('_', ' ')
                        );
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' },
                        '& .MuiSvgIcon-root': { color: '#8b949e' },
                      }}
                    >
                      {contentTypes.content_types?.map((type) => (
                        <MenuItem
                          key={type}
                          value={type}
                          sx={{ color: '#f0f6fc' }}
                        >
                          {type.charAt(0).toUpperCase() +
                            type.slice(1).replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Tone */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <Select
                      value={postForm.tone || ''}
                      onChange={(e) =>
                        handlePostFormChange('tone', e.target.value)
                      }
                      displayEmpty
                      renderValue={(value) => {
                        if (!value)
                          return (
                            <span style={{ color: '#7d8590' }}>
                              Select Tone
                            </span>
                          );
                        return value.charAt(0).toUpperCase() + value.slice(1);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' },
                        '& .MuiSvgIcon-root': { color: '#8b949e' },
                      }}
                    >
                      {contentTypes.tones?.map((tone) => (
                        <MenuItem
                          key={tone}
                          value={tone}
                          sx={{ color: '#f0f6fc' }}
                        >
                          {tone.charAt(0).toUpperCase() + tone.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Variations */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Variations"
                    value={postForm.num_variations}
                    onChange={(e) =>
                      handlePostFormChange(
                        'num_variations',
                        parseInt(e.target.value)
                      )
                    }
                    inputProps={{ min: 1, max: 10 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1a1a1a',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#30363d' },
                        '&:hover fieldset': { borderColor: '#58a6ff' },
                        '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                      },
                      '& .MuiInputLabel-root': { color: '#8b949e' },
                      '& .MuiInputBase-input': { color: '#f0f6fc' },
                    }}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Key Message Input */}
            <Card
              sx={{
                p: 4,
                mb: 4,
                bgcolor: '#21262d',
                border: '1px solid #30363d',
              }}
            >
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Key Message"
                value={postForm.key_message}
                onChange={(e) =>
                  handlePostFormChange('key_message', e.target.value)
                }
                placeholder="Enter your key message or topic for content generation..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1a1a1a',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: '#30363d' },
                    '&:hover fieldset': { borderColor: '#58a6ff' },
                    '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                  },
                  '& .MuiInputLabel-root': { color: '#8b949e' },
                  '& .MuiInputBase-input': { color: '#f0f6fc' },
                }}
              />
            </Card>

            {/* Generate Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Button
                variant="contained"
                startIcon={<Create />}
                onClick={handlePostGeneration}
                disabled={loading}
                sx={{
                  bgcolor: '#238636',
                  color: '#ffffff',
                  '&:hover': { bgcolor: '#2ea043' },
                  py: 2,
                  px: 6,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                ) : (
                  'Generate Posts'
                )}
              </Button>
            </Box>

            {/* Generated Posts */}
            {generatedPosts.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: '#f0f6fc', mb: 3, fontWeight: 600 }}
                >
                  Generated Posts
                </Typography>
                {generatedPosts.map((post, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 3,
                      bgcolor: '#21262d',
                      border: '1px solid #30363d',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ color: '#f0f6fc' }}>
                          {post.title}
                        </Typography>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                          {post.predicted_score != null && (
                            <Chip
                              label={`Score ${Math.round(post.predicted_score)}`}
                              size="small"
                              sx={{
                                bgcolor:
                                  post.predicted_score >= 70
                                    ? '#238636'
                                    : post.predicted_score >= 40
                                    ? '#d29922'
                                    : '#7d8590',
                                color: '#ffffff',
                                fontWeight: 500,
                              }}
                            />
                          )}
                          <Chip
                            label={post.estimated_engagement}
                            size="small"
                            sx={{
                              bgcolor:
                                post.estimated_engagement === 'high'
                                  ? '#238636'
                                  : post.estimated_engagement === 'medium'
                                  ? '#f78166'
                                  : '#7d8590',
                              color: '#ffffff',
                              fontWeight: 500,
                            }}
                          />
                          <Tooltip title="Copy to clipboard">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(post.content)}
                              sx={{ color: '#58a6ff' }}
                            >
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{ mb: 2, color: '#c9d1d9', lineHeight: 1.6 }}
                      >
                        {post.content}
                      </Typography>

                      {post.hashtags && post.hashtags.length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          {post.hashtags.map((hashtag, hashtagIndex) => (
                            <Chip
                              key={hashtagIndex}
                              label={hashtag}
                              size="small"
                              sx={{
                                bgcolor: '#161b22',
                                color: '#58a6ff',
                                border: '1px solid #30363d',
                                fontWeight: 500,
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      {post.call_to_action && (
                        <Typography
                          variant="body2"
                          sx={{ color: '#7d8590', mb: 2 }}
                        >
                          <strong>Call to Action:</strong> {post.call_to_action}
                        </Typography>
                      )}

                      {post.predicted_signals && post.predicted_signals.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                          <Typography variant="caption" sx={{ color: '#7d8590', width: '100%', mb: 0.5 }}>
                            Improvement tips:
                          </Typography>
                          {post.predicted_signals.map((sig) => (
                            <Chip
                              key={sig}
                              size="small"
                              label={
                                sig === 'caption_too_long'
                                  ? 'Caption too long'
                                  : sig === 'weak_cta'
                                  ? 'Add CTA'
                                  : sig === 'suboptimal_posting_time'
                                  ? 'Try peak hours'
                                  : sig
                              }
                              sx={{
                                bgcolor: 'rgba(210, 153, 34, 0.2)',
                                color: '#d29922',
                                fontSize: '0.7rem',
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleKeepAsPost(post)}
                          disabled={loading}
                          sx={{
                            borderColor: '#58a6ff',
                            color: '#58a6ff',
                            '&:hover': {
                              borderColor: '#1f6feb',
                              bgcolor: 'rgba(88, 166, 255, 0.1)',
                            },
                            textTransform: 'none',
                          }}
                        >
                          Keep as Post
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => copyToClipboard(post.content)}
                          sx={{
                            borderColor: '#f78166',
                            color: '#f78166',
                            '&:hover': {
                              borderColor: '#f0883e',
                              bgcolor: 'rgba(247, 129, 102, 0.1)',
                            },
                            textTransform: 'none',
                          }}
                        >
                          Copy Content
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Generate Hashtags Tab */}
        {activeTab === 1 && (
          <Box>
            <Typography
              variant="h6"
              sx={{ color: '#f0f6fc', mb: 4, fontWeight: 600 }}
            >
              Generate Hashtags
            </Typography>

            {/* Form Controls */}
            <Card
              sx={{
                p: 4,
                mb: 4,
                bgcolor: '#21262d',
                border: '1px solid #30363d',
              }}
            >
              <Grid container spacing={3} justifyContent="center">
                {/* Client Selection */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <Select
                      value={hashtagForm.client_id || ''}
                      onChange={(e) =>
                        handleHashtagFormChange('client_id', e.target.value)
                      }
                      displayEmpty
                      renderValue={(value) => {
                        if (!value)
                          return (
                            <span style={{ color: '#7d8590' }}>
                              Select Client
                            </span>
                          );
                        const client = clients.find((c) => c.id === value);
                        return client ? client.name : '';
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' },
                        '& .MuiSvgIcon-root': { color: '#8b949e' },
                      }}
                    >
                      {clients.map((client) => (
                        <MenuItem
                          key={client.id}
                          value={client.id}
                          sx={{ color: '#f0f6fc' }}
                        >
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Platform Selection */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <Select
                      value={hashtagForm.platform || ''}
                      onChange={(e) =>
                        handleHashtagFormChange('platform', e.target.value)
                      }
                      displayEmpty
                      renderValue={(value) => {
                        if (!value)
                          return (
                            <span style={{ color: '#7d8590' }}>
                              Select Platform
                            </span>
                          );
                        return value.charAt(0).toUpperCase() + value.slice(1);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#58a6ff' },
                          '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' },
                        '& .MuiSvgIcon-root': { color: '#8b949e' },
                      }}
                    >
                      {contentTypes.platforms?.map((platform) => (
                        <MenuItem
                          key={platform}
                          value={platform}
                          sx={{ color: '#f0f6fc' }}
                        >
                          {platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Number of Hashtags */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Hashtags"
                    value={hashtagForm.num_hashtags}
                    onChange={(e) =>
                      handleHashtagFormChange(
                        'num_hashtags',
                        parseInt(e.target.value)
                      )
                    }
                    inputProps={{ min: 1, max: 50 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#1a1a1a',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#30363d' },
                        '&:hover fieldset': { borderColor: '#58a6ff' },
                        '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                      },
                      '& .MuiInputLabel-root': { color: '#8b949e' },
                      '& .MuiInputBase-input': { color: '#f0f6fc' },
                    }}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Content Input */}
            <Card
              sx={{
                p: 4,
                mb: 4,
                bgcolor: '#21262d',
                border: '1px solid #30363d',
              }}
            >
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Content"
                value={hashtagForm.content}
                onChange={(e) =>
                  handleHashtagFormChange('content', e.target.value)
                }
                placeholder="Enter your content to generate relevant hashtags..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1a1a1a',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: '#30363d' },
                    '&:hover fieldset': { borderColor: '#58a6ff' },
                    '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
                  },
                  '& .MuiInputLabel-root': { color: '#8b949e' },
                  '& .MuiInputBase-input': { color: '#f0f6fc' },
                }}
              />
            </Card>

            {/* Generate Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Button
                variant="contained"
                startIcon={<Tag />}
                onClick={handleHashtagGeneration}
                disabled={loading}
                sx={{
                  bgcolor: '#238636',
                  color: '#ffffff',
                  '&:hover': { bgcolor: '#2ea043' },
                  py: 2,
                  px: 6,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                ) : (
                  'Generate Hashtags'
                )}
              </Button>
            </Box>

            {/* Generated Hashtags */}
            {generatedHashtags.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: '#f0f6fc', mb: 3, fontWeight: 600 }}
                >
                  Generated Hashtags
                </Typography>
                <Card sx={{ bgcolor: '#21262d', border: '1px solid #30363d' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mb: 3,
                      }}
                    >
                      {generatedHashtags.map((hashtag, index) => (
                        <Chip
                          key={index}
                          label={hashtag}
                          size="medium"
                          sx={{
                            bgcolor: '#161b22',
                            color: '#58a6ff',
                            border: '1px solid #30363d',
                            fontWeight: 500,
                            '&:hover': {
                              bgcolor: '#21262d',
                              borderColor: '#58a6ff',
                            },
                          }}
                          onClick={() => copyToClipboard(hashtag)}
                          clickable
                        />
                      ))}
                    </Box>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() =>
                        copyToClipboard(generatedHashtags.join(' '))
                      }
                      sx={{
                        borderColor: '#58a6ff',
                        color: '#58a6ff',
                        '&:hover': {
                          borderColor: '#1f6feb',
                          bgcolor: 'rgba(88, 166, 255, 0.1)',
                        },
                        textTransform: 'none',
                      }}
                    >
                      Copy All Hashtags
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AIContent;
